import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";
import { analytics } from "@/lib/analytics";
import { sanitizePathname } from "@/lib/analytics/error-utils";
import { findNavigationItem, generateBreadcrumbs } from "@/routers/utils";

const AnalyticsRouteTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousRouteRef = useRef<{ path: string; name: string } | null>(null);

  const pathname = location.pathname || "/";
  const breadcrumbs = useMemo(() => generateBreadcrumbs(pathname), [pathname]);
  const pageName = useMemo(() => {
    if (breadcrumbs.length > 0) {
      return breadcrumbs[breadcrumbs.length - 1].title;
    }

    const navItem = findNavigationItem(pathname);
    return navItem?.title || pathname;
  }, [breadcrumbs, pathname]);
  const routePath = useMemo(() => {
    const navItem = findNavigationItem(pathname);
    return navItem?.path || sanitizePathname(pathname);
  }, [pathname]);

  useEffect(() => {
    analytics.trackPageView(pageName, {
      route_path: routePath,
      page_depth: breadcrumbs.length,
      is_dynamic: routePath.includes(":"),
    });
  }, [pageName, routePath, breadcrumbs.length]);

  useEffect(() => {
    const previous = previousRouteRef.current;
    if (!previous) {
      previousRouteRef.current = { path: routePath, name: pageName };
      return;
    }

    if (previous.path === routePath) {
      return;
    }

    const startTime = typeof performance !== "undefined" ? performance.now() : Date.now();
    const next = { path: routePath, name: pageName };
    previousRouteRef.current = next;

    const report = () => {
      const endTime = typeof performance !== "undefined" ? performance.now() : Date.now();
      const duration = endTime - startTime;
      if (!Number.isFinite(duration) || duration <= 0) return;

      analytics.trackPerformance({
        metricName: "route.change.render",
        duration,
        metadata: {
          from_route_path: previous.path,
          to_route_path: next.path,
          from_page_name: previous.name,
          to_page_name: next.name,
          navigation_type: navigationType,
          is_dynamic: next.path.includes(":"),
          route_path: next.path,
        },
      });
    };

    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(() => {
        requestAnimationFrame(report);
      });
    } else {
      setTimeout(report, 0);
    }
  }, [routePath, pageName, navigationType]);

  return null;
};

export default AnalyticsRouteTracker;
